import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import AthleteDashboard from "../components/AthleteDashboard";
import AthleteViewPreview from "../components/AthleteViewPreview";
import TodayProgramsCard from "../components/dashboard/TodayProgramsCard";
import { useLanguage } from "../contexts/LanguageContext";
import {
	ClipboardList,
	Dumbbell,
	Users,
	UserPlus,
	Copy,
	ChevronRight,
	Loader2,
	ExternalLink,
} from "lucide-react";
import { fetchAPI } from "../lib/utils";
import {
	DEFAULT_EXERCISE_CATEGORIES,
	getCategoryColor,
	normalizeCategoryValue,
} from "../lib/coachUtils";
import { getProgramVisibilityStats } from "../lib/programVisibility";
import {
	buildProgramPreview,
	buildTodayProgramEntries,
	describeRecipients,
	programPreviewPath,
} from "../lib/todayPrograms";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../components/ui/select";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "../components/ui/tooltip";

export default function Dashboard() {
	const { user } = useAuth();
	const { t, language } = useLanguage();
	const navigate = useNavigate();
	const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
	const [inviteGroupId, setInviteGroupId] = useState("__none__");
	const [inviteProgramId, setInviteProgramId] = useState("__none__");
	const [inviteGroups, setInviteGroups] = useState([]);
	const [invitePrograms, setInvitePrograms] = useState([]);
	const [inviteLoadingOptions, setInviteLoadingOptions] = useState(false);
	const [inviteGenerating, setInviteGenerating] = useState(false);
	const [generatedInviteLink, setGeneratedInviteLink] = useState("");
	const [generatedInviteExpiresAt, setGeneratedInviteExpiresAt] = useState("");
	// In-flight/completed compact programs request, shared between the dashboard
	// counters and the invite dialog so the list is fetched only once.
	const programsRequestRef = useRef(null);
	// Lo stesso per i gruppi: servono a «Oggi» (per chi e' il programma) e alla
	// finestra d'invito.
	const groupsRequestRef = useRef(null);
	const inviteOptionsRequestRef = useRef(null);
	const isMountedRef = useRef(true);

	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[],
	);

	// Admin users and coaches see the CoachDashboard - use is_admin from backend
	const isAdmin = user?.is_admin || user?.role === "admin";
	const isCoach = user?.role === "coach" || isAdmin;

	const [dashboardStats, setDashboardStats] = useState(null);
	const [dashboardStatsLoading, setDashboardStatsLoading] = useState(false);
	const [dashboardLookup, setDashboardLookup] = useState(null);
	const [exerciseLibrary, setExerciseLibrary] = useState([]);
	const [todayState, setTodayState] = useState({
		entries: [],
		loading: true,
		error: false,
	});
	const [todayReloadKey, setTodayReloadKey] = useState(0);
	const [groupsById, setGroupsById] = useState({});
	const [todayPreview, setTodayPreview] = useState(null);

	useEffect(() => {
		if (!isCoach) return;
		let cancelled = false;
		const loadStats = async () => {
			setDashboardStatsLoading(true);
			try {
				programsRequestRef.current = fetchAPI(
					"/programs?scope=mine&compact=true",
				).catch(() => []);
				const [
					programsData,
					athletesData,
					exercisesData,
					categoriesData,
					conditioningData,
				] = await Promise.all([
					programsRequestRef.current,
					fetchAPI("/users/athletes").catch(() => []),
					fetchAPI("/exercises").catch(() => []),
					fetchAPI("/exercises/categories").catch(() => []),
					fetchAPI("/conditioning-library").catch(() => []),
				]);
				if (cancelled) return;
				const programs = Array.isArray(programsData) ? programsData : [];
				// La libreria serve anche all'anteprima di «Oggi» (nomi e media
				// degli esercizi): stessa lista, nessuna richiesta in piu'.
				const allAthletes = Array.isArray(athletesData) ? athletesData : [];
				const regularExercises = Array.isArray(exercisesData)
					? exercisesData
					: [];
				const configuredCategories = Array.isArray(categoriesData)
					? categoriesData
					: [];
				const conditioningTemplates = Array.isArray(conditioningData)
					? conditioningData
					: [];
				setExerciseLibrary(regularExercises);

				// Filter athletes directly assigned to this coach only
				const myId = user?.user_id;
				const athletes = myId
					? allAthletes.filter((a) => {
							const directIds = Array.isArray(a.coach_ids)
								? a.coach_ids
								: a.coach_id
									? [a.coach_id]
									: [];
							return directIds.includes(myId);
						})
					: allAthletes;

				const twoWeeksAgo = Date.now() - 14 * 24 * 60 * 60 * 1000;
				const activeAthletes = athletes.filter((a) => {
					if (!a.last_access_at) return false;
					const ts = new Date(a.last_access_at).getTime();
					return !Number.isNaN(ts) && ts >= twoWeeksAgo;
				});

				const activeProgramsList = programs.filter((p) => {
					const hasAthletes =
						(Array.isArray(p.athlete_ids) && p.athlete_ids.length > 0) ||
						(Array.isArray(p.athlete_assignments) &&
							p.athlete_assignments.length > 0);
					if (!hasAthletes) return false;
					if (!p.is_active) return false;
					return getProgramVisibilityStats(p).hasVisibleWeeks;
				});
				const inactiveProgramsList = programs.filter(
					(program) => !activeProgramsList.includes(program),
				);

				const normalizedCategories =
					configuredCategories.length > 0
						? configuredCategories.map((category, index) => ({
								value: normalizeCategoryValue(
									category?.value || category?.name || category?.label || "",
								),
								label:
									category?.label || category?.name || category?.value || "",
								order:
									typeof category?.order === "number" ? category.order : index,
							}))
						: DEFAULT_EXERCISE_CATEGORIES.map((category, index) => ({
								...category,
								value: normalizeCategoryValue(category.value),
								order: index,
							}));

				const categoryMap = new Map();
				normalizedCategories.forEach((category, index) => {
					if (!category?.value) return;
					if (categoryMap.has(category.value)) return;
					categoryMap.set(category.value, {
						value: category.value,
						label: category.label || category.value,
						order: typeof category.order === "number" ? category.order : index,
						items: [],
						count: 0,
					});
				});
				if (!categoryMap.has("conditioning")) {
					categoryMap.set("conditioning", {
						value: "conditioning",
						label: "Conditioning",
						order: categoryMap.size,
						items: [],
						count: 0,
					});
				}

				regularExercises.forEach((exercise) => {
					const categoryValue = normalizeCategoryValue(exercise.category);
					const bucket = categoryMap.get(categoryValue);
					if (!bucket) return;
					bucket.items.push({
						...exercise,
						_sourceType: "exercise",
					});
					bucket.count += 1;
				});

				conditioningTemplates.forEach((template) => {
					const bucket = categoryMap.get("conditioning");
					bucket.items.push({
						...template,
						category: "conditioning",
						_sourceType: "conditioning-library",
					});
					bucket.count += 1;
				});

				const exerciseCategories = Array.from(categoryMap.values()).sort(
					(a, b) => {
						if (a.order !== b.order) return a.order - b.order;
						return a.label.localeCompare(b.label, "it");
					},
				);
				const regularExerciseCategories = exerciseCategories.filter(
					(category) => category.value !== "conditioning",
				);

				const conditioningTypesMap = new Map();
				conditioningTemplates.forEach((template) => {
					const rawType = String(template?.conditioning_type || "custom")
						.trim()
						.toLowerCase();
					const type = rawType || "custom";
					if (!conditioningTypesMap.has(type)) {
						conditioningTypesMap.set(type, {
							value: type,
							label: type.toUpperCase(),
							count: 0,
							items: [],
						});
					}
					const bucket = conditioningTypesMap.get(type);
					bucket.items.push({
						...template,
						_sourceType: "conditioning-library",
					});
					bucket.count += 1;
				});
				const conditioningTypeBreakdown = Array.from(
					conditioningTypesMap.values(),
				).sort((a, b) => a.label.localeCompare(b.label, "it"));

				const regularExercisesPersonal = regularExercises.filter(
					(exercise) =>
						String(exercise?.library_scope || "")
							.trim()
							.toLowerCase() !== "global",
				).length;
				const regularExercisesGlobal = regularExercises.filter(
					(exercise) =>
						String(exercise?.library_scope || "")
							.trim()
							.toLowerCase() === "global",
				).length;
				const personalExerciseIds = new Set(
					regularExercises
						.filter(
							(exercise) =>
								String(exercise?.library_scope || "")
									.trim()
									.toLowerCase() !== "global",
						)
						.map((exercise) => String(exercise?.exercise_id || "").trim())
						.filter(Boolean),
				);
				const regularExercisesSharedToGlobal = regularExercises.filter(
					(exercise) => {
						const scope = String(exercise?.library_scope || "")
							.trim()
							.toLowerCase();
						if (scope !== "global") return false;
						const sourceExerciseId = String(
							exercise?.source_exercise_id || "",
						).trim();
						return (
							sourceExerciseId && personalExerciseIds.has(sourceExerciseId)
						);
					},
				).length;
				const conditioningPersonal = conditioningTemplates.filter(
					(template) =>
						String(template?.library_scope || "")
							.trim()
							.toLowerCase() !== "global",
				).length;
				const conditioningGlobal = conditioningTemplates.filter(
					(template) =>
						String(template?.library_scope || "")
							.trim()
							.toLowerCase() === "global",
				).length;

				const sortedAthletes = [...athletes].sort((a, b) =>
					String(a.name || a.email || a.user_id || "").localeCompare(
						String(b.name || b.email || b.user_id || ""),
						"it",
					),
				);
				const activeAthleteIds = new Set(
					activeAthletes.map((athlete) => athlete.user_id),
				);
				const inactiveAthletes = sortedAthletes.filter(
					(athlete) => !activeAthleteIds.has(athlete.user_id),
				);
				const sortedActiveAthletes = sortedAthletes.filter((athlete) =>
					activeAthleteIds.has(athlete.user_id),
				);
				const sortedPrograms = [...programs].sort((a, b) =>
					String(
						a.title || a.name || a.program_title || a.program_id || "",
					).localeCompare(
						String(b.title || b.name || b.program_title || b.program_id || ""),
						"it",
					),
				);
				const activeProgramIds = new Set(
					activeProgramsList.map((program) => program.program_id),
				);
				const sortedActivePrograms = sortedPrograms.filter((program) =>
					activeProgramIds.has(program.program_id),
				);
				const sortedInactivePrograms = sortedPrograms.filter(
					(program) => !activeProgramIds.has(program.program_id),
				);

				setDashboardStats({
					programsTotal: programs.length,
					programsActive: activeProgramsList.length,
					programsInactive: inactiveProgramsList.length,
					programsActiveList: sortedActivePrograms,
					programsInactiveList: sortedInactivePrograms,
					athletesTotal: athletes.length,
					athletesActive: activeAthletes.length,
					athletesInactive: inactiveAthletes.length,
					athletesActiveList: sortedActiveAthletes,
					athletesInactiveList: inactiveAthletes,
					exercisesTotal:
						regularExercises.length + conditioningTemplates.length,
					exercisesRegularTotal: regularExercises.length,
					exercisesRegularPersonal: regularExercisesPersonal,
					exercisesRegularSharedToGlobal: regularExercisesSharedToGlobal,
					exercisesRegularGlobal: regularExercisesGlobal,
					conditioningTotal: conditioningTemplates.length,
					conditioningPersonal,
					conditioningGlobal,
					exerciseCategories,
					regularExerciseCategories,
					conditioningTypeBreakdown,
				});
			} catch (err) {
				console.error("Failed to load dashboard stats", err);
			} finally {
				if (!cancelled) setDashboardStatsLoading(false);
			}
		};
		loadStats();
		return () => {
			cancelled = true;
		};
	}, [isAdmin, isCoach, user?.user_id]);

	// «Oggi»: i programmi miei e quelli condivisi con me (un trainer li riceve
	// attraverso un gruppo condiviso), dall'elenco compatto. Il programma intero
	// arriva solo quando se ne apre uno. Spec: docs/coach-today/01-analisi.md.
	useEffect(() => {
		if (!isCoach) return;
		let cancelled = false;
		setTodayState((prev) => ({ ...prev, loading: true, error: false }));
		(async () => {
			try {
				if (!groupsRequestRef.current) {
					groupsRequestRef.current = fetchAPI("/groups");
				}
				const [mine, shared, groups] = await Promise.all([
					programsRequestRef.current ||
						fetchAPI("/programs?scope=mine&compact=true"),
					fetchAPI("/programs?scope=shared&compact=true"),
					groupsRequestRef.current.catch(() => []),
				]);
				if (cancelled) return;
				const groupMap = {};
				(Array.isArray(groups) ? groups : []).forEach((group) => {
					if (group?.group_id) groupMap[group.group_id] = group;
				});
				setGroupsById(groupMap);
				setTodayState({
					entries: buildTodayProgramEntries(
						[
							...(Array.isArray(mine) ? mine : []),
							...(Array.isArray(shared) ? shared : []),
						],
						{ userId: user?.user_id, groupsById: groupMap },
					),
					loading: false,
					error: false,
				});
			} catch {
				if (cancelled) return;
				// Riprovando si rifanno tutte le richieste, non quella in cache.
				programsRequestRef.current = null;
				groupsRequestRef.current = null;
				setTodayState({ entries: [], loading: false, error: true });
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [isCoach, user?.user_id, todayReloadKey]);

	const openProgramPreview = useCallback(async ({ program, dayId = null }) => {
		if (!program?.program_id) return;
		setDashboardLookup(null);
		setTodayPreview({ program: null, source: program, dayId, loading: true, error: false });
		try {
			const full = await fetchAPI(programPreviewPath(program));
			setTodayPreview((prev) =>
				prev?.source?.program_id === program.program_id
					? { ...prev, program: buildProgramPreview(full), loading: false }
					: prev,
			);
		} catch {
			setTodayPreview((prev) =>
				prev?.source?.program_id === program.program_id
					? { ...prev, loading: false, error: true }
					: prev,
			);
		}
	}, []);

	// Memorizzato: un oggetto nuovo a ogni render riporterebbe l'anteprima sul
	// giorno di partenza mentre il coach sta navigando fra i giorni.
	const todayPreviewState = useMemo(() => {
		const program = todayPreview?.program;
		if (!program || (program.weeks || []).length === 0) return null;
		const dayId = todayPreview.dayId;
		const weekIndex = dayId
			? Math.max(
					program.weeks.findIndex((week) =>
						(week?.days || []).some((day) => day?.day_id === dayId),
					),
					0,
				)
			: 0;
		return {
			program,
			programs: [program],
			exercises: exerciseLibrary,
			selectedProgramId: program.program_id,
			weekIndex,
			dayId,
		};
	}, [todayPreview, exerciseLibrary]);

	const todayLabel = useMemo(
		() =>
			new Date().toLocaleDateString(language === "en" ? "en-US" : "it-IT", {
				weekday: "long",
				day: "numeric",
				month: "long",
			}),
		[language],
	);

	const previewRecipients = useMemo(() => {
		if (!todayPreview?.source) return [];
		const { groupNames, athleteCount } = describeRecipients(
			todayPreview.source,
			groupsById,
		);
		const parts = [...groupNames];
		if (athleteCount > 0) {
			parts.push(
				athleteCount === 1
					? t("todayCardAthleteOne")
					: t("todayCardAthletes").replace("{count}", athleteCount),
			);
		}
		return parts;
	}, [todayPreview, groupsById, t]);

	const loadInviteOptions = useCallback(() => {
		if (!isCoach || inviteOptionsRequestRef.current) return;

		setInviteLoadingOptions(true);
		inviteOptionsRequestRef.current = (async () => {
			let failed = false;
			const onError = () => {
				failed = true;
				return [];
			};
			const [groups, programs] = await Promise.all([
				(groupsRequestRef.current || fetchAPI("/groups")).catch(onError),
				// Reuse the compact list already requested for the counters
				(
					programsRequestRef.current ||
					fetchAPI("/programs?scope=mine&compact=true")
				).catch(onError),
			]);

			if (isMountedRef.current) {
				setInviteGroups(Array.isArray(groups) ? groups : []);
				setInvitePrograms(Array.isArray(programs) ? programs : []);
				setInviteLoadingOptions(false);
			}
			// Keep the cache only on success, so a failure retries on next open
			if (failed) inviteOptionsRequestRef.current = null;
		})();
	}, [isCoach]);

	useEffect(() => {
		if (!inviteDialogOpen) return;
		loadInviteOptions();
	}, [inviteDialogOpen, loadInviteOptions]);

	const openAthleteLookup = (event, type) => {
		event.stopPropagation();
		const isActive = type === "active";
		const items = isActive
			? dashboardStats?.athletesActiveList || []
			: dashboardStats?.athletesInactiveList || [];
		setDashboardLookup({
			kind: "athletes",
			title: isActive ? "Atleti attivi" : "Atleti inattivi",
			description: isActive
				? "Elenco email degli atleti con accesso negli ultimi 14 giorni"
				: "Elenco email degli atleti inattivi o senza accessi recenti",
			items,
		});
	};

	const openProgramLookup = (event, type) => {
		event.stopPropagation();
		const isActive = type === "active";
		const items = isActive
			? dashboardStats?.programsActiveList || []
			: dashboardStats?.programsInactiveList || [];
		setDashboardLookup({
			kind: isActive ? "active-programs" : "programs",
			title: isActive ? "Programmi attivi" : "Programmi inattivi",
			description: isActive
				? "Programmi con atleti assegnati e settimane utilizzabili"
				: "Programmi senza atleti o non piu attivi",
			items,
		});
	};

	const openExerciseCategoryLookup = (event, category) => {
		event.stopPropagation();
		event.preventDefault();
		setDashboardLookup({
			kind: "exercise-categories",
			title: category.label,
			description:
				category.value === "conditioning"
					? "Elementi presenti nella conditioning library"
					: `Esercizi della categoria ${category.label}`,
			items: category.items || [],
		});
	};

	const openConditioningTypeLookup = (event, typeBucket) => {
		event.stopPropagation();
		event.preventDefault();
		setDashboardLookup({
			kind: "exercise-categories",
			title: `Conditioning ${typeBucket.label}`,
			description: `Elementi conditioning di tipo ${typeBucket.label}`,
			items: typeBucket.items || [],
		});
	};

	const renderLookupValue = (item) => {
		if (dashboardLookup?.kind === "athletes") {
			return item.email || item.name || item.user_id || "Atleta senza email";
		}
		if (dashboardLookup?.kind === "exercise-categories") {
			return (
				item.name ||
				item.exercise_name ||
				item.exercise_id ||
				"Esercizio senza nome"
			);
		}
		return (
			item.title ||
			item.name ||
			item.program_title ||
			item.program_id ||
			"Programma senza nome"
		);
	};

	const renderLookupMeta = (item) => {
		if (dashboardLookup?.kind === "athletes") {
			if (item.name && item.email && item.name !== item.email) {
				return item.name;
			}
			return item.last_access_at
				? `Ultimo accesso ${new Date(item.last_access_at).toLocaleDateString("it-IT")}`
				: "Mai entrato";
		}
		if (dashboardLookup?.kind === "exercise-categories") {
			if (item._sourceType === "conditioning-library") {
				return item.conditioning_type
					? `Conditioning library • ${item.conditioning_type.toUpperCase()}`
					: "Conditioning library";
			}
			return item.description || "Libreria esercizi";
		}
		const athleteCount = Array.isArray(item.athlete_assignments)
			? item.athlete_assignments.length
			: Array.isArray(item.athlete_ids)
				? item.athlete_ids.length
				: 0;
		return athleteCount > 0
			? `${athleteCount} atleti assegnati`
			: "Nessun atleta assegnato";
	};

	const handleCardNavigation = (path) => {
		navigate(path);
	};

	const buildExercisesRoute = ({ scope = null, category = null } = {}) => {
		const params = new URLSearchParams();
		params.set("from", "dashboard");
		if (scope) params.set("scope", scope);
		if (category) params.set("category", category);
		return `/exercises?${params.toString()}`;
	};

	const openExercisesPreset = (
		event,
		{ scope = null, category = null } = {},
	) => {
		event.stopPropagation();
		event.preventDefault();
		handleCardNavigation(buildExercisesRoute({ scope, category }));
	};

	const handleCardKeyDown = (event, path) => {
		if (event.key === "Enter" || event.key === " ") {
			event.preventDefault();
			handleCardNavigation(path);
		}
	};

	const handleGenerateInviteLink = async () => {
		setInviteGenerating(true);
		try {
			const payload = {
				group_id: inviteGroupId === "__none__" ? null : inviteGroupId,
				program_id: inviteProgramId === "__none__" ? null : inviteProgramId,
			};
			const data = await fetchAPI("/invites", {
				method: "POST",
				body: JSON.stringify(payload),
			});

			const invitePath = data?.invite_path || "";
			const fullLink = invitePath
				? `${window.location.origin}${invitePath}`
				: "";
			setGeneratedInviteLink(fullLink);
			setGeneratedInviteExpiresAt(data?.expires_at || "");
			toast.success(t("inviteLinkReady"));
		} catch (error) {
			toast.error(error.message || t("error"));
		} finally {
			setInviteGenerating(false);
		}
	};

	const formatInviteExpiryDateTime = (isoDate) => {
		if (!isoDate) return "-";
		const parsed = new Date(isoDate);
		if (Number.isNaN(parsed.getTime())) return "-";
		return parsed.toLocaleString(language === "en" ? "en-US" : "it-IT", {
			year: "numeric",
			month: "short",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const handleCopyInviteLink = async () => {
		if (!generatedInviteLink) return;
		try {
			await navigator.clipboard.writeText(generatedInviteLink);
			toast.success(t("inviteLinkCopied"));
		} catch (_e) {
			toast.error(t("error"));
		}
	};

	if (!user) {
		return (
			<div className="min-h-screen bg-background flex items-center justify-center">
				<div className="spinner"></div>
			</div>
		);
	}

	if (!isCoach) {
		return <AthleteDashboard />;
	}

	const sortedExerciseCategories = Array.isArray(
		dashboardStats?.regularExerciseCategories,
	)
		? dashboardStats.regularExerciseCategories
		: [];
	const sortedConditioningTypes = Array.isArray(
		dashboardStats?.conditioningTypeBreakdown,
	)
		? dashboardStats.conditioningTypeBreakdown
		: [];

	return (
		<div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 md:py-8">
			<div className="mb-6">
				<h1 className="font-heading text-3xl md:text-4xl mb-2">Dashboard</h1>
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
				{/* Oggi: prima di tutto, poi l'invito, poi i conteggi */}
				<TodayProgramsCard
					className="order-first md:col-span-2"
					entries={todayState.entries}
					loading={todayState.loading}
					error={todayState.error}
					onRetry={() => setTodayReloadKey((key) => key + 1)}
					onOpen={(entry) =>
						openProgramPreview({ program: entry.program, dayId: entry.dayId })
					}
					todayLabel={todayLabel}
				/>

				{/* Invita atleta */}
				<Dialog
					open={inviteDialogOpen}
					onOpenChange={(open) => {
						setInviteDialogOpen(open);
						if (!open) {
							setInviteGroupId("__none__");
							setInviteProgramId("__none__");
							setGeneratedInviteLink("");
							setGeneratedInviteExpiresAt("");
						}
					}}
				>
					<DialogTrigger asChild>
						<button
							type="button"
							onPointerEnter={loadInviteOptions}
							onPointerDown={loadInviteOptions}
							onFocus={loadInviteOptions}
							className="order-first group w-full text-left bg-card border border-border rounded-sm p-4 md:p-6 hover:border-volt/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 touch-manipulation active:scale-[0.995] transition-transform flex flex-col gap-3 md:gap-5 md:col-span-2"
						>
							<div className="flex items-start justify-between gap-4">
								<div className="flex items-center gap-2">
									<UserPlus className="w-5 h-5 text-volt" />
									<span className="label-tactical text-muted-foreground">
										{t("inviteAthleteLink")}
									</span>
								</div>
								<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
							</div>
							<div className="space-y-2 md:hidden">
								<p className="font-heading text-lg leading-tight">
									{t("inviteAthleteLinkIntro")}
								</p>
								<p className="text-xs text-muted-foreground leading-relaxed">
									{t("inviteAthleteLinkDescription")}
								</p>
							</div>
							<div className="hidden md:grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)] gap-5 items-start">
								<div className="space-y-3">
									<p className="font-heading text-2xl md:text-3xl leading-tight">
										{t("inviteAthleteLinkIntro")}
									</p>
									<p className="text-sm text-muted-foreground max-w-2xl">
										{t("inviteAthleteLinkDescription")}
									</p>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
										<div className="rounded-sm border border-border bg-secondary/40 px-4 py-3">
											<p className="label-tactical text-muted-foreground mb-2">
												1
											</p>
											<p className="text-sm text-foreground">
												{t("inviteCreateStepSelect")}
											</p>
										</div>
										<div className="rounded-sm border border-border bg-secondary/40 px-4 py-3">
											<p className="label-tactical text-muted-foreground mb-2">
												2
											</p>
											<p className="text-sm text-foreground">
												{t("inviteCreateStepGenerate")}
											</p>
										</div>
										<div className="rounded-sm border border-border bg-secondary/40 px-4 py-3">
											<p className="label-tactical text-muted-foreground mb-2">
												3
											</p>
											<p className="text-sm text-foreground">
												{t("inviteCreateStepSend")}
											</p>
										</div>
									</div>
								</div>
								<div className="rounded-sm border border-volt/20 bg-volt/5 px-4 py-4 space-y-2">
									<p className="label-tactical text-volt/80">
										{t("inviteCreateOutcomeTitle")}
									</p>
									<p className="text-sm text-foreground leading-relaxed">
										{t("inviteCreateOutcome")}
									</p>
								</div>
							</div>
						</button>
					</DialogTrigger>

					<DialogContent className="bg-card border-border max-w-lg">
						<DialogHeader>
							<DialogTitle className="font-heading">
								{t("createInviteLink")}
							</DialogTitle>
							<DialogDescription className="text-muted-foreground">
								{t("inviteDialogDescription")}
							</DialogDescription>
						</DialogHeader>

						<div className="space-y-4">
							<div className="rounded-sm border border-border bg-secondary/40 px-4 py-4 space-y-2">
								<p className="label-tactical text-muted-foreground">
									{t("inviteHowItWorksTitle")}
								</p>
								<div className="space-y-2 text-sm text-foreground/90">
									<p>{t("inviteCreateStepSelect")}</p>
									<p>{t("inviteCreateStepGenerate")}</p>
									<p>{t("inviteCreateStepSend")}</p>
								</div>
								<div className="mt-2 rounded-sm border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
									<p className="font-medium">{t("inviteExpiryNoticeTitle")}</p>
									<p className="mt-1">{t("inviteExpiryNoticeBody")}</p>
								</div>
							</div>

							<div className="space-y-2">
								<p className="label-tactical">{t("inviteGroupOptional")}</p>
								<Select
									value={inviteGroupId}
									onValueChange={setInviteGroupId}
									disabled={inviteLoadingOptions}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm">
										{inviteLoadingOptions ? (
											<span className="flex items-center gap-2 text-muted-foreground">
												<Loader2 className="w-3.5 h-3.5 animate-spin" />
												{t("loading")}
											</span>
										) : (
											<SelectValue />
										)}
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="__none__">
											{t("noGroupOption")}
										</SelectItem>
										{inviteGroups.map((group) => (
											<SelectItem key={group.group_id} value={group.group_id}>
												{group.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<p className="label-tactical">{t("inviteProgramOptional")}</p>
								<Select
									value={inviteProgramId}
									onValueChange={setInviteProgramId}
									disabled={inviteLoadingOptions}
								>
									<SelectTrigger className="bg-secondary border-transparent rounded-sm">
										{inviteLoadingOptions ? (
											<span className="flex items-center gap-2 text-muted-foreground">
												<Loader2 className="w-3.5 h-3.5 animate-spin" />
												{t("loading")}
											</span>
										) : (
											<SelectValue />
										)}
									</SelectTrigger>
									<SelectContent className="bg-card border-border">
										<SelectItem value="__none__">
											{t("noProgramOption")}
										</SelectItem>
										{invitePrograms.map((program) => (
											<SelectItem
												key={program.program_id}
												value={program.program_id}
											>
												{program.title || program.name || program.program_id}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<Button
								onClick={handleGenerateInviteLink}
								disabled={inviteGenerating || inviteLoadingOptions}
								className="w-full bg-volt hover:bg-volt/90 rounded-sm"
							>
								{inviteGenerating
									? t("inviteCreating")
									: t("generateInviteLink")}
							</Button>

							{generatedInviteLink ? (
								<div className="space-y-3 rounded-sm border border-volt/20 bg-volt/5 px-4 py-4">
									<div>
										<p className="label-tactical text-volt/80">
											{t("inviteLinkReady")}
										</p>
										<p className="mt-2 text-xs text-muted-foreground leading-relaxed">
											{t("inviteGeneratedHelp")}
										</p>
										<p className="mt-2 text-xs text-amber-200">
											{t("inviteExpiresAtLabel")}: {formatInviteExpiryDateTime(generatedInviteExpiresAt)}
										</p>
									</div>
									<div className="flex items-center gap-2">
										<Input
											readOnly
											value={generatedInviteLink}
											className="bg-secondary border-transparent rounded-sm text-xs"
										/>
										<Button
											variant="outline"
											className="border-dashed border-volt/40 text-volt rounded-sm"
											onClick={handleCopyInviteLink}
										>
											<Copy className="w-4 h-4 mr-2" />
											{t("copyInviteLink")}
										</Button>
									</div>
								</div>
							) : null}
						</div>
					</DialogContent>
				</Dialog>

				{/* Programmi */}
				<div
					role="link"
					tabIndex={0}
					aria-label={t("programs")}
					onClick={() => handleCardNavigation("/programs")}
					onKeyDown={(event) => handleCardKeyDown(event, "/programs")}
					className="order-1 md:order-none group bg-card border border-border rounded-sm p-4 md:p-6 hover:border-volt/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 touch-manipulation active:scale-[0.995] transition-transform flex flex-col gap-3 md:gap-5 cursor-pointer"
				>
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-2">
							<ClipboardList className="w-5 h-5 text-volt" />
							<span className="label-tactical text-muted-foreground">
								{t("programs")}
							</span>
						</div>
						<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
					</div>
					{dashboardStatsLoading ? (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							<span className="text-sm">Caricamento...</span>
						</div>
					) : (
						<>
							<div className="pt-2 md:pt-3 border-t border-border space-y-2 md:space-y-4">
								<div className="flex items-baseline justify-between gap-3">
									<p className="label-tactical text-muted-foreground">Totale</p>
									<p className="font-heading text-xl md:text-2xl leading-none tabular-nums text-muted-foreground">
										{dashboardStats?.programsTotal ?? "–"}
									</p>
								</div>
								<div className="grid grid-cols-2 gap-2 md:gap-3">
									<button
										type="button"
										onClick={(event) => openProgramLookup(event, "active")}
										className="text-left rounded-sm border border-volt/20 bg-volt/5 px-3 py-3 md:px-4 md:py-4 hover:border-volt/40"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats?.programsActive ?? "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											Attivi
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											Atleti assegnati + settimane attive
										</p>
									</button>
									<button
										type="button"
										onClick={(event) => openProgramLookup(event, "inactive")}
										className="text-left rounded-sm border border-border bg-secondary/40 px-3 py-3 md:px-4 md:py-4 hover:border-border/80"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats != null
												? dashboardStats.programsInactive
												: "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											Inattivi
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											Senza atleti o non piu attivi
										</p>
									</button>
								</div>
							</div>
						</>
					)}
				</div>

				{/* Atleti */}
				<div
					role="link"
					tabIndex={0}
					aria-label={t("athletes")}
					onClick={() => handleCardNavigation("/athletes")}
					onKeyDown={(event) => handleCardKeyDown(event, "/athletes")}
					className="order-2 md:order-none group bg-card border border-border rounded-sm p-4 md:p-6 hover:border-volt/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 touch-manipulation active:scale-[0.995] transition-transform flex flex-col gap-3 md:gap-5 cursor-pointer"
				>
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-2">
							<Users className="w-5 h-5 text-volt" />
							<span className="label-tactical text-muted-foreground">
								{t("athletes")}
							</span>
						</div>
						<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
					</div>
					{dashboardStatsLoading ? (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							<span className="text-sm">Caricamento...</span>
						</div>
					) : (
						<>
							<div className="space-y-2 md:space-y-4 pt-2 md:pt-3 border-t border-border">
								<div className="flex items-baseline justify-between gap-3">
									<p className="label-tactical text-muted-foreground">Totale</p>
									<p className="font-heading text-xl md:text-2xl leading-none tabular-nums text-muted-foreground">
										{dashboardStats?.athletesTotal ?? "–"}
									</p>
								</div>
								<div className="grid grid-cols-2 gap-2 md:gap-3">
									<TooltipProvider delayDuration={80}>
										<Tooltip>
											<TooltipTrigger asChild>
												<button
													type="button"
													onClick={(event) =>
														openAthleteLookup(event, "active")
													}
													className="w-full text-left rounded-sm border border-acid/20 bg-acid/5 px-3 py-3 md:px-4 md:py-4 hover:border-acid/40"
												>
													<p className="font-heading text-3xl md:text-4xl leading-none text-acid tabular-nums">
														{dashboardStats?.athletesActive ?? "–"}
													</p>
													<div className="mt-2 md:mt-3 flex items-center gap-2">
														<p className="text-sm font-semibold text-foreground">
															Attivi
														</p>
														<span className="text-[10px] label-tactical border border-border px-1.5 py-0.5 text-muted-foreground">
															14g
														</span>
													</div>
													<p className="mt-1 text-xs text-muted-foreground">
														Clicca per vedere le email
													</p>
												</button>
											</TooltipTrigger>
											<TooltipContent
												side="bottom"
												className="max-w-[220px]"
											>
												Atleti con almeno un accesso negli ultimi 14 giorni
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
									<button
										type="button"
										onClick={(event) => openAthleteLookup(event, "inactive")}
										className="text-left rounded-sm border border-border bg-secondary/40 px-3 py-3 md:px-4 md:py-4 hover:border-border/80"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats?.athletesInactive ?? "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											Inattivi
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											Clicca per vedere le email
										</p>
									</button>
								</div>
								{dashboardStats?.athletesTotal > 0 && (
									<div className="w-full h-1.5 bg-secondary rounded-full overflow-hidden mt-1">
										<div
											className="h-full bg-acid rounded-full"
											style={{
												width: `${Math.round(
													(dashboardStats.athletesActive /
														dashboardStats.athletesTotal) *
														100,
												)}%`,
											}}
										/>
									</div>
								)}
							</div>
						</>
					)}
				</div>

				{/* Esercizi */}
				<div
					role="link"
					tabIndex={0}
					aria-label={t("exercises")}
					onClick={() =>
						handleCardNavigation(buildExercisesRoute({ category: "all" }))
					}
					onKeyDown={(event) =>
						handleCardKeyDown(event, buildExercisesRoute({ category: "all" }))
					}
					className="order-3 md:order-none group bg-card border border-border rounded-sm p-4 md:p-6 hover:border-volt/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 touch-manipulation active:scale-[0.995] transition-transform flex flex-col gap-3 md:gap-5 cursor-pointer"
				>
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-2">
							<Dumbbell className="w-5 h-5 text-volt" />
							<span className="label-tactical text-muted-foreground">
								{t("exercises")}
							</span>
						</div>
						<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
					</div>
					{dashboardStatsLoading ? (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							<span className="text-sm">Caricamento...</span>
						</div>
					) : (
						<>
							<div className="space-y-2 md:space-y-4 pt-2 md:pt-3 border-t border-border">
								<div className="flex items-baseline justify-between gap-3">
									<p className="label-tactical text-muted-foreground">Totale</p>
									<p className="font-heading text-xl md:text-2xl leading-none tabular-nums text-muted-foreground">
										{dashboardStats?.exercisesRegularTotal ?? "–"}
									</p>
								</div>
								<div className="grid grid-cols-2 gap-2 md:gap-3">
									<button
										type="button"
										onClick={(event) =>
											openExercisesPreset(event, {
												scope: "personal",
												category: "all",
											})
										}
										className="text-left rounded-sm border border-emerald-500/20 bg-emerald-500/5 px-3 py-3 md:px-4 md:py-4 hover:border-emerald-500/40 w-full"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats?.exercisesRegularPersonal ?? "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											{t("dashboardMyPersonalLibrary")}
										</p>
										<p className="mt-1 text-xs text-muted-foreground">
											{t("dashboardSharedToGlobalLabel")}:{" "}
											{dashboardStats?.exercisesRegularSharedToGlobal ?? 0}
										</p>
									</button>
									<button
										type="button"
										onClick={(event) =>
											openExercisesPreset(event, {
												scope: "global",
												category: "all",
											})
										}
										className="text-left rounded-sm border border-indigo-500/20 bg-indigo-500/5 px-3 py-3 md:px-4 md:py-4 hover:border-indigo-500/40 w-full"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats?.exercisesRegularGlobal ?? "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											{t("dashboardGlobalLibrary")}
										</p>
									</button>
								</div>
							</div>
							<div className="hidden sm:block pt-2 md:pt-3 border-t border-border">
								<p className="label-tactical text-muted-foreground mb-2">
									PER CATEGORIA
								</p>
								{sortedExerciseCategories.length > 0 ? (
									<div className="flex flex-wrap gap-2">
										{sortedExerciseCategories.map((category) => {
											const colorClass = getCategoryColor(category.value);
											return (
												<button
													type="button"
													key={category.value}
													onClick={(event) =>
														openExerciseCategoryLookup(event, category)
													}
													className={`inline-flex items-center gap-2 text-[11px] font-medium px-2.5 py-1.5 rounded-sm border border-white/10 hover:border-white/20 ${colorClass}`}
												>
													{category.label}
													<span className="font-heading font-bold text-sm leading-none">
														{category.count}
													</span>
												</button>
											);
										})}
									</div>
								) : (
									<p className="text-xs text-muted-foreground">
										Nessun esercizio censito
									</p>
								)}
							</div>
						</>
					)}
				</div>

				{/* Conditioning */}
				<div
					role="link"
					tabIndex={0}
					aria-label="Conditioning"
					onClick={() => handleCardNavigation("/conditioning-library")}
					onKeyDown={(event) =>
						handleCardKeyDown(event, "/conditioning-library")
					}
					className="order-4 md:order-none group bg-card border border-border rounded-sm p-4 md:p-6 hover:border-volt/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 touch-manipulation active:scale-[0.995] transition-transform flex flex-col gap-3 md:gap-5 cursor-pointer"
				>
					<div className="flex items-start justify-between">
						<div className="flex items-center gap-2">
							<Dumbbell className="w-5 h-5 text-volt" />
							<span className="label-tactical text-muted-foreground">
								Conditioning
							</span>
						</div>
						<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
					</div>
					{dashboardStatsLoading ? (
						<div className="flex items-center gap-2 text-muted-foreground">
							<Loader2 className="w-4 h-4 animate-spin" />
							<span className="text-sm">Caricamento...</span>
						</div>
					) : (
						<>
							<div className="space-y-2 md:space-y-4 pt-2 md:pt-3 border-t border-border">
								<div className="flex items-baseline justify-between gap-3">
									<p className="label-tactical text-muted-foreground">Totale</p>
									<p className="font-heading text-xl md:text-2xl leading-none tabular-nums text-muted-foreground">
										{dashboardStats?.conditioningTotal ?? "–"}
									</p>
								</div>
								<div className="grid grid-cols-2 gap-2 md:gap-3">
									<button
										type="button"
										onClick={(event) =>
											openExercisesPreset(event, {
												scope: "personal",
												category: "conditioning",
											})
										}
										className="text-left rounded-sm border border-emerald-500/20 bg-emerald-500/5 px-3 py-3 md:px-4 md:py-4 hover:border-emerald-500/40 w-full"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats?.conditioningPersonal ?? "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											{t("dashboardMyPersonalLibrary")}
										</p>
									</button>
									<button
										type="button"
										onClick={(event) =>
											openExercisesPreset(event, {
												scope: "global",
												category: "conditioning",
											})
										}
										className="text-left rounded-sm border border-indigo-500/20 bg-indigo-500/5 px-3 py-3 md:px-4 md:py-4 hover:border-indigo-500/40 w-full"
									>
										<p className="font-heading text-3xl md:text-4xl leading-none text-foreground tabular-nums">
											{dashboardStats?.conditioningGlobal ?? "–"}
										</p>
										<p className="mt-2 md:mt-3 text-sm font-semibold text-foreground">
											{t("dashboardGlobalLibrary")}
										</p>
									</button>
								</div>
							</div>
							<div className="hidden sm:block pt-2 md:pt-3 border-t border-border">
								<p className="label-tactical text-muted-foreground mb-2">
									PER TIPO
								</p>
								{sortedConditioningTypes.length > 0 ? (
									<div className="flex flex-wrap gap-2">
										{sortedConditioningTypes.map((typeBucket) => (
											<button
												type="button"
												key={typeBucket.value}
												onClick={(event) =>
													openConditioningTypeLookup(event, typeBucket)
												}
												className="inline-flex items-center gap-2 text-[11px] font-medium px-2.5 py-1.5 rounded-sm border border-cyan-500/20 bg-cyan-500/10 text-cyan-200 hover:border-cyan-500/40"
											>
												{typeBucket.label}
												<span className="font-heading font-bold text-sm leading-none">
													{typeBucket.count}
												</span>
											</button>
										))}
									</div>
								) : (
									<p className="text-xs text-muted-foreground">
										Nessun conditioning censito
									</p>
								)}
							</div>
						</>
					)}
				</div>

				<Dialog
					open={!!dashboardLookup}
					onOpenChange={(open) => {
						if (!open) setDashboardLookup(null);
					}}
				>
					<DialogContent className="bg-card border-border max-w-xl">
						<DialogHeader>
							<DialogTitle className="font-heading">
								{dashboardLookup?.title}
							</DialogTitle>
							<DialogDescription className="text-muted-foreground">
								{dashboardLookup?.description}
							</DialogDescription>
						</DialogHeader>

						<div className="max-h-[55vh] overflow-y-auto space-y-2 pr-1">
							{dashboardLookup?.items?.length ? (
								dashboardLookup.items.map((item) => {
									const primary = renderLookupValue(item);
									const meta = renderLookupMeta(item);
									const key =
										item.user_id || item.program_id || `${primary}-${meta}`;
									// I programmi attivi si aprono nella vista atleta, come
									// dal blocco «Oggi»: stessa cosa, stesso gesto.
									if (dashboardLookup?.kind === "active-programs") {
										return (
											<button
												type="button"
												key={key}
												onClick={() => openProgramPreview({ program: item })}
												className="group w-full text-left flex items-center gap-3 rounded-sm border border-border bg-secondary/30 px-4 py-3 min-h-[56px] hover:border-volt/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50"
											>
												<div className="min-w-0 flex-1">
													<p className="text-sm font-medium text-foreground break-words">
														{primary}
													</p>
													<p className="mt-1 text-xs text-muted-foreground">
														{meta}
													</p>
												</div>
												<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
											</button>
										);
									}
									return (
										<div
											key={key}
											className="rounded-sm border border-border bg-secondary/30 px-4 py-3"
										>
											<p className="text-sm font-medium text-foreground break-all">
												{primary}
											</p>
											<p className="mt-1 text-xs text-muted-foreground">
												{meta}
											</p>
										</div>
									);
								})
							) : (
								<p className="text-sm text-muted-foreground">
									Nessun elemento disponibile
								</p>
							)}
						</div>
					</DialogContent>
				</Dialog>

				<AthleteViewPreview
					open={!!todayPreview}
					onOpenChange={(open) => {
						if (!open) setTodayPreview(null);
					}}
					title={
						todayPreview?.source?.title || todayPreview?.source?.name || ""
					}
					description={previewRecipients.join(" · ") || null}
					headerActions={
						todayPreview?.source?.program_id ? (
							<Link
								to={
									todayPreview.source.coach_id === user?.user_id
										? `/programs/${todayPreview.source.program_id}`
										: `/programs/${todayPreview.source.program_id}/view`
								}
								className="inline-flex items-center gap-1.5 self-start text-sm text-volt hover:underline underline-offset-4 min-h-[44px] md:min-h-0"
							>
								<ExternalLink className="w-3.5 h-3.5" />
								{t("todayPreviewOpenProgram")}
							</Link>
						) : null
					}
					previewState={todayPreviewState}
					loading={Boolean(todayPreview?.loading)}
					emptyLabel={
						todayPreview?.error
							? t("todayPreviewError")
							: t("todayPreviewEmpty")
					}
				/>
			</div>
		</div>
	);
}
